import { useMemo, useState, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer,
} from 'recharts';
import { ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import type { SensorDetail } from '../../types';
import { useSensorPoints, RANGE_DURATION_MS } from '../../hooks/useSensorPoints';
import { useSensorAlertHistory } from '../../hooks/useSensorAlertHistory';
import { useSensorNotes } from '../../hooks/useSensorNotes';
import { RangeToggle, type ChartRange } from './RangeToggle';
import { DetailSection } from './DetailSection';
import type { AlertHistoryRow } from '../../hooks/useSensorAlertHistory';
import { CHART_COLORS } from '@/components/charts/types';

// =============================================================================
// Helpers
// =============================================================================

function relativeTime(ts: string | null | undefined): string {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function alertStatusLabel(status: string | undefined): string {
  if (!status) return 'unknown';
  if (status === 'open' || status === 'created') return 'Open';
  if (status === 'closed') return 'Closed';
  return status;
}

function formatAckedBy(ack: AlertHistoryRow['acknowledged_by']): string {
  if (!ack) return '—';
  if (typeof ack === 'string') return ack;
  const parts = [ack.firstname, ack.lastname].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function ruleLabel(row: AlertHistoryRow): string {
  if (row.rule_signature) {
    const [, condition, target] = row.rule_signature.split('|');
    if (condition && target) return `${condition} ${target}`;
  }
  if (row.rulegroup?.name) return row.rulegroup.name;
  return '—';
}

// "Now" clamped to the nearest 5-minute boundary so the query key is stable
// while the user browses — avoids a refetch on every render tick.
function nowBoundary(): Date {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
  return d;
}

// =============================================================================
// Stats strip computation (excludes nulls)
// =============================================================================

interface StatsStrip {
  min: number | null;
  max: number | null;
  avg: number | null;
  stddev: number | null;
  uptime: number | null;
  n: number;
}

function computeStats(points: Array<{ value: number | null }>): StatsStrip {
  const total = points.length;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const n = values.length;
  if (n === 0) return { min: null, max: null, avg: null, stddev: null, uptime: null, n: total };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, v) => a + (v - avg) ** 2, 0) / n;
  const stddev = Math.sqrt(variance);
  return { min, max, avg, stddev, uptime: total > 0 ? (n / total) * 100 : null, n: total };
}

// =============================================================================
// Chart data builders
// =============================================================================

interface ChartPoint {
  name: string;   // x-axis label
  value?: number; // null/undefined = gap in chart
  rangeMin?: number;
  rangeMax?: number;
}

function buildChartData(
  points: Array<{ ts: string; value: number | null }>,
  rangeMin?: number,
  rangeMax?: number,
): ChartPoint[] {
  return points.map(({ ts, value }) => {
    const label = new Date(ts).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Montreal',
    });
    const pt: ChartPoint = { name: label };
    if (value !== null) pt.value = value;
    if (rangeMin !== undefined) pt.rangeMin = rangeMin;
    if (rangeMax !== undefined) pt.rangeMax = rangeMax;
    return pt;
  });
}

// =============================================================================
// Sub-components
// =============================================================================

function StatItem({ label, value }: { label: string; value: string | null }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value ?? '—'}</span>
    </span>
  );
}

function StatDivider() {
  return <span className="text-border select-none" aria-hidden>▏</span>;
}

// =============================================================================
// Main component
// =============================================================================

interface SensorDetailBodyProps {
  sensor: SensorDetail;
  open: boolean;
}

const ALERTS_PAGE_SIZE = 5;
// Threshold: if the window end is within this many ms of now, treat it as "at latest"
const AT_LATEST_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

export function SensorDetailBody({ sensor, open }: SensorDetailBodyProps) {
  const [range, setRange] = useState<ChartRange>('7d');
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  // Pan state: explicit ISO strings for start/end.
  // Initialize end=now, start=now-range.
  const [windowEnd, setWindowEnd] = useState<Date>(() => nowBoundary());
  const [windowStart, setWindowStart] = useState<Date>(() => {
    const e = nowBoundary();
    return new Date(e.getTime() - RANGE_DURATION_MS['7d']);
  });

  const mongoId = sensor._id;
  const unit = sensor.sensortype?.unit ?? '';
  const configuredMin = (sensor as unknown as Record<string, unknown>).min_value as number | undefined;
  const configuredMax = (sensor as unknown as Record<string, unknown>).max_value as number | undefined;

  // ── Derived "at latest" flag ────────────────────────────────────────────────
  const isAtLatest = useMemo(
    () => Date.now() - windowEnd.getTime() < AT_LATEST_THRESHOLD_MS,
    [windowEnd],
  );

  // ── Range toggle: reset window to [now-range, now] ─────────────────────────
  const handleRangeChange = useCallback((newRange: ChartRange) => {
    setRange(newRange);
    const end = nowBoundary();
    setWindowEnd(end);
    setWindowStart(new Date(end.getTime() - RANGE_DURATION_MS[newRange]));
  }, []);

  // ── Pan back ◀: shift window backward by one range duration ────────────────
  const handlePanBack = useCallback(() => {
    const dur = RANGE_DURATION_MS[range];
    setWindowEnd((prev) => new Date(prev.getTime() - dur));
    setWindowStart((prev) => new Date(prev.getTime() - dur));
  }, [range]);

  // ── Pan forward ▶: shift window forward (clamped so end ≤ now+1min) ────────
  const handlePanForward = useCallback(() => {
    const dur = RANGE_DURATION_MS[range];
    const newEnd = new Date(windowEnd.getTime() + dur);
    const cap = new Date(Date.now() + 60_000);
    const clampedEnd = newEnd > cap ? cap : newEnd;
    const clampedStart = new Date(clampedEnd.getTime() - dur);
    setWindowEnd(clampedEnd);
    setWindowStart(clampedStart);
  }, [range, windowEnd]);

  // ── Latest ⤓: reset to now ──────────────────────────────────────────────────
  const handleLatest = useCallback(() => {
    const end = nowBoundary();
    setWindowEnd(end);
    setWindowStart(new Date(end.getTime() - RANGE_DURATION_MS[range]));
  }, [range]);

  // ── Data hooks ──────────────────────────────────────────────────────────────
  const pointsQuery = useSensorPoints(
    mongoId,
    windowStart.toISOString(),
    windowEnd.toISOString(),
    open,
  );
  const alertsQuery = useSensorAlertHistory(mongoId, open);
  const notesQuery = useSensorNotes(mongoId, open);

  // ── Stats (over current window) ─────────────────────────────────────────────
  const stats = useMemo(
    () => computeStats(pointsQuery.data ?? []),
    [pointsQuery.data],
  );

  // ── Chart data ───────────────────────────────────────────────────────────────
  const chartData = useMemo(
    () => buildChartData(pointsQuery.data ?? [], configuredMin, configuredMax),
    [pointsQuery.data, configuredMin, configuredMax],
  );

  const hasThresholds = configuredMin !== undefined || configuredMax !== undefined;

  // ── Alerts ───────────────────────────────────────────────────────────────────
  const alerts = alertsQuery.data ?? [];
  const openAlerts = alerts.filter((a) => a.status === 'open' || a.status === 'created');
  const closedAlerts = alerts.filter((a) => a.status === 'closed');
  const sortedAlerts = [...openAlerts, ...closedAlerts];
  const visibleAlerts = alertsExpanded ? sortedAlerts : sortedAlerts.slice(0, ALERTS_PAGE_SIZE);

  const nowValue =
    sensor.value !== undefined && sensor.value !== null
      ? `${sensor.value.toFixed(2)}${unit ? ` ${unit}` : ''}`
      : null;
  const lastUpdate = relativeTime(sensor.timestamp);

  const notes = notesQuery.data ?? [];

  return (
    <div className="space-y-0 divide-y divide-border">

      {/* ====== STATS STRIP ====== */}
      <div className="px-4 py-2.5 bg-muted/30">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
          {nowValue && (
            <>
              <StatItem label="NOW" value={nowValue} />
              <StatDivider />
            </>
          )}
          {lastUpdate && (
            <>
              <span className="text-xs text-muted-foreground">{lastUpdate}</span>
              <StatDivider />
            </>
          )}
          <StatItem label="min" value={stats.min !== null ? stats.min.toFixed(2) : null} />
          <StatDivider />
          <StatItem label="max" value={stats.max !== null ? stats.max.toFixed(2) : null} />
          <StatDivider />
          <StatItem label="avg" value={stats.avg !== null ? stats.avg.toFixed(2) : null} />
          <StatDivider />
          <StatItem label="σ" value={stats.stddev !== null ? stats.stddev.toFixed(2) : null} />
          <StatDivider />
          <StatItem label="up" value={stats.uptime !== null ? `${stats.uptime.toFixed(1)}%` : null} />
          <StatDivider />
          <StatItem label="n" value={String(stats.n)} />
        </div>
        {pointsQuery.isLoading && (
          <Skeleton className="h-3 w-full mt-1 rounded" />
        )}
      </div>

      {/* ====== VALUE CHART ====== */}
      <div className="px-4 pt-3 pb-2">
        {/* Toolbar: label | RangeToggle | ◀ ▶ Latest */}
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
            Value {unit ? `(${unit})` : ''}
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            <RangeToggle value={range} onChange={handleRangeChange} />
            <div className="flex items-center" role="group" aria-label="Pan chart window">
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 rounded-r-none border-r-0"
                onClick={handlePanBack}
                aria-label="Previous window"
                disabled={pointsQuery.isFetching}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 rounded-l-none border-r-0"
                onClick={handlePanForward}
                aria-label="Next window"
                disabled={isAtLatest || pointsQuery.isFetching}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs rounded-l-none"
                onClick={handleLatest}
                aria-label="Jump to latest"
                disabled={isAtLatest || pointsQuery.isFetching}
              >
                <ChevronsRight className="h-3 w-3 mr-0.5" />
                Latest
              </Button>
            </div>
          </div>
        </div>

        {/* Chart */}
        {pointsQuery.isLoading ? (
          <Skeleton className="h-[240px] w-full rounded" />
        ) : pointsQuery.isError ? (
          <div className="h-[240px] flex items-center justify-center text-sm text-destructive">
            Failed to load chart data
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">
            No data for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={36}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '11px',
                }}
              />
              {hasThresholds && <Legend wrapperStyle={{ fontSize: '11px' }} />}
              {/* Main value line — connectNulls=false leaves gaps for null points */}
              <Line
                type="monotone"
                dataKey="value"
                name="Value"
                stroke={CHART_COLORS.primary}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
              {configuredMax !== undefined && (
                <Line
                  type="step"
                  dataKey="rangeMax"
                  name="Max"
                  stroke={CHART_COLORS.danger}
                  strokeWidth={1}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                />
              )}
              {configuredMin !== undefined && (
                <Line
                  type="step"
                  dataKey="rangeMin"
                  name="Min"
                  stroke={CHART_COLORS.warning}
                  strokeWidth={1}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                />
              )}
              {/* Brush for client-side fine zoom/scrub within the loaded window */}
              <Brush
                dataKey="name"
                height={20}
                stroke="#d1d5db"
                fill="#f9fafb"
                travellerWidth={6}
                tickFormatter={() => ''}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* Configured threshold annotation */}
        {hasThresholds && (
          <p className="text-xs text-muted-foreground mt-1">
            Configured range: {configuredMin ?? '?'} … {configuredMax ?? '?'} {unit}
          </p>
        )}
      </div>

      {/* ====== ALERTS TABLE ====== */}
      <div className="px-4 pt-3 pb-2">
        <DetailSection title="Alerts" count={alerts.length}>
          {alertsQuery.isLoading ? (
            <div className="space-y-2 py-1">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
            </div>
          ) : alertsQuery.isError ? (
            <p className="text-xs text-destructive py-2">Failed to load alerts</p>
          ) : alerts.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No alert history</p>
          ) : (
            <>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs py-1.5 px-2 w-[70px]">Status</TableHead>
                      <TableHead className="text-xs py-1.5 px-2">Rule</TableHead>
                      <TableHead className="text-xs py-1.5 px-2 w-[90px]">Opened</TableHead>
                      <TableHead className="text-xs py-1.5 px-2 w-[80px]">Ack'd</TableHead>
                      <TableHead className="text-xs py-1.5 px-2 w-[90px]">Closed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleAlerts.map((alert) => {
                      const isOpen = alert.status === 'open' || alert.status === 'created';
                      return (
                        <TableRow
                          key={alert._id}
                          className={cn('text-xs', isOpen && 'bg-red-50/40 dark:bg-red-950/20')}
                        >
                          <TableCell className="py-1 px-2">
                            <Badge
                              variant={isOpen ? 'destructive' : 'secondary'}
                              className="text-xs px-1.5 py-0 normal-case font-medium"
                            >
                              {alertStatusLabel(alert.status)}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="py-1 px-2 max-w-[120px] truncate"
                            title={ruleLabel(alert)}
                          >
                            {ruleLabel(alert)}
                          </TableCell>
                          <TableCell className="py-1 px-2 tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                            {alert.opened_at ? formatDate(alert.opened_at) : '—'}
                          </TableCell>
                          <TableCell className="py-1 px-2 tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                            {alert.acknowledged_at
                              ? formatAckedBy(alert.acknowledged_by)
                              : '—'}
                          </TableCell>
                          <TableCell className="py-1 px-2 tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                            {alert.closed_at ? formatDate(alert.closed_at) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {sortedAlerts.length > ALERTS_PAGE_SIZE && (
                <button
                  type="button"
                  onClick={() => setAlertsExpanded((v) => !v)}
                  className="mt-1.5 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {alertsExpanded
                    ? 'Show less'
                    : `${sortedAlerts.length - ALERTS_PAGE_SIZE} more …`}
                </button>
              )}
            </>
          )}
        </DetailSection>
      </div>

      {/* ====== NOTES ====== */}
      <div className="px-4 pt-3 pb-4">
        <DetailSection title="Notes" count={notes.length}>
          {notesQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded" />
              ))}
            </div>
          ) : notesQuery.isError ? (
            <p className="text-xs text-destructive py-2">Failed to load notes</p>
          ) : notes.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No notes</p>
          ) : (
            <div className="space-y-2">
              {notes.map((note, idx) => {
                const authorParts = note.user
                  ? [note.user.firstname, note.user.lastname].filter(Boolean)
                  : [];
                const author = authorParts.length ? authorParts.join(' ') : '—';
                const dateStr = note.createdAt ?? '';
                return (
                  <div
                    // v3 endpoint doesn't return _id; use index as fallback key
                    key={dateStr ? `${dateStr}-${idx}` : idx}
                    className="border rounded-md px-3 py-2 bg-muted/20 text-sm"
                  >
                    <div className="flex items-center gap-1.5 mb-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{author}</span>
                      {dateStr && (
                        <>
                          <span>·</span>
                          <span title={formatDateTime(dateStr)}>{formatDate(dateStr)}</span>
                        </>
                      )}
                    </div>
                    {note.comment ? (
                      <div
                        className="text-sm [&_p]:my-0.5 [&_h4]:text-sm [&_h4]:font-semibold [&_ul]:list-disc [&_ul]:pl-4 [&_li]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: note.comment }}
                      />
                    ) : (
                      <p className="text-muted-foreground italic">No content</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DetailSection>
      </div>
    </div>
  );
}
